import { useState } from 'react'
import { useTelegram } from '../../hooks/useTelegram'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'
import SubscriptionModal from '../modals/SubscriptionModal'

function Avatar({ user }) {
  const initial = user?.first_name?.[0]?.toUpperCase() ?? '@'

  if (user?.photo_url) {
    return (
      <img
        src={user.photo_url}
        alt="avatar"
        style={{
          width: 54, height: 54, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: '2px solid rgba(255,107,53,0.4)',
        }}
      />
    )
  }

  return (
    <div style={{
      width: 54, height: 54, borderRadius: '50%',
      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, fontSize: 20, fontWeight: 700, color: '#fff',
      border: '2px solid rgba(16, 185, 129, 0.3)',
      boxShadow: '0 0 12px rgba(16, 185, 129, 0.2)',
    }}>
      {initial}
    </div>
  )
}

export default function UserProfile() {
  const { user } = useTelegram()
  const t = useT()
  const subscriptionEnd = useDashboardStore(s => s.subscriptionEnd)
  const isAdmin = useDashboardStore(s => s.isAdmin)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ')
    : ''
  const username = user?.username ? `@${user.username}` : ''
  const userId   = user?.id ?? null

  // Честные fallback'ы вместо фейковых '@username_example' / '01.01.2027':
  // если данных от Telegram или бэкенда нет — показываем нейтральную
  // заглушку, а не выдуманные значения.
  const primaryName = username || fullName || t.userFallback
  const secondaryName = username && fullName ? fullName : null

  const displaySubscription = subscriptionEnd

  return (
    <>
      <div
        className="mb-5 mt-1"
        data-hint="profile-card"
        style={{
          background: '#1A1A1A',
          border: '1px solid rgba(255,255,255,0.04)',
          borderRadius: 16,
          padding: '14px 16px',
        }}
      >
        {/* Top row: avatar + name */}
        <div className="flex items-center gap-4" style={{ marginBottom: 10 }}>
          <Avatar user={user} />

          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3 }} className="truncate">
              {primaryName}
            </span>

            {secondaryName && (
              <span style={{ fontSize: 12, color: '#8E8E93' }}>{secondaryName}</span>
            )}

            {userId != null && (
              <span style={{ fontSize: 12, color: '#636366' }}>
                ID: <span style={{ color: '#8E8E93' }}>{userId}</span>
              </span>
            )}
          </div>
        </div>

        {/* Subscription status. Для админа — бессрочный доступ (без даты
            окончания), кнопка «Подписка» не нужна. */}
        <div style={{ fontSize: 12, color: '#636366', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isAdmin
            ? <span style={{ color: '#34D399', fontWeight: 700 }}>🛡 {t.adminNoExpiry}</span>
            : displaySubscription
              ? (<><span style={{ color: '#8E8E93' }}>{t.subscriptionUntil}</span> <span style={{ color: '#34D399', fontWeight: 600 }}>{displaySubscription}</span></>)
              : <span style={{ color: '#8E8E93' }}>{t.subscriptionInactive}</span>}
        </div>

        {/* Full-width subscription button. Кнопка видна и админу — ему нужно
            проверять работу раздела «Подписка» (оплата, проверка хэша и т.д.). */}
        {(
          <button
            onClick={() => setShowSubscriptionModal(true)}
            data-hint="subscribe-btn"
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: 12,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
            }}
          >
            {t.subscription ?? 'Подписка'}
          </button>
        )}
      </div>

      {showSubscriptionModal && (
        <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} />
      )}
    </>
  )
}
